import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import { BarChart2, TrendingUp } from 'lucide-react';
import { analyticsAPI } from '../services/api';

const GranularTrendsChart = ({ keyword = 'Singapore PR', country = 'SG' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [granularInfo, setGranularInfo] = useState(null);

  useEffect(() => {
    const fetchGranularData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await analyticsAPI.getGranularCharts(keyword, country);
        setChartData(response.granular_chart);
        setGranularInfo({
          granularity: response.granularity,
          dataPoints: response.data_points,
          dateRange: response.date_range,
          stats: response.stats
        });
      } catch (err) {
        console.error('Error loading granular chart data:', err);
        setError('Failed to load granular trends data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (keyword && country) {
      fetchGranularData();
    }
  }, [keyword, country]);

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="card-title mb-0 d-flex align-items-center">
            <BarChart2 className="text-info me-2" size={20} />
            Google Trends: {keyword} (12-Month Granular View)
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="info" />
          <p className="mt-3 text-muted">Loading granular data...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <h5 className="card-title mb-0 d-flex align-items-center">
            <BarChart2 className="text-info me-2" size={20} />
            Google Trends: {keyword} (12-Month Granular View)
          </h5>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-warning">
            <strong>Warning:</strong> {error}
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!chartData) {
    return null;
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0 d-flex align-items-center">
            <BarChart2 className="text-info me-2" size={20} />
            Google Trends: {keyword} (12-Month Granular View)
          </h5>
          {granularInfo && (
            <div>
              <Badge bg="info" className="me-2">
                {granularInfo.granularity}
              </Badge>
              <Badge bg="secondary">
                {granularInfo.dataPoints} data points
              </Badge>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {granularInfo && (
          <Row className="mb-3">
            <Col md={3}>
              <small className="text-muted">Date Range</small>
              <div className="fw-bold">
                {new Date(granularInfo.dateRange.start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {new Date(granularInfo.dateRange.end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            </Col>
            <Col md={2}>
              <small className="text-muted">Avg Interest</small>
              <div className="fw-bold">
                <TrendingUp size={14} className="text-primary me-1" />
                {granularInfo.stats.avg_interest}
              </div>
            </Col>
            <Col md={2}>
              <small className="text-muted">Max</small>
              <div className="fw-bold text-success">{granularInfo.stats.max_interest}</div>
            </Col>
            <Col md={2}>
              <small className="text-muted">Min</small>
              <div className="fw-bold text-danger">{granularInfo.stats.min_interest}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Latest Value</small>
              <div className="fw-bold text-primary">{granularInfo.stats.latest_value}</div>
            </Col>
          </Row>
        )}
        <div className="chart-container">
          <Plot
            data={JSON.parse(chartData).data}
            layout={{
              ...JSON.parse(chartData).layout,
              autosize: true,
              responsive: true,
            }}
            config={{
              responsive: true,
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
            }}
            style={{ width: '100%', height: '500px' }}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default GranularTrendsChart;
